import { Breakpoints } from '@angular/cdk/layout';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { API } from '@app-core/constants/api.constants';
import { TripDetailsViewType } from '../models/access.model';
import { CognitoRegionType, Duration, EULA_CONFIG } from '@app-core/models/core.model';
import * as moment from 'moment';
import 'moment-timezone';
import { environment } from '@env/environment';

export interface Cache {
  url: string;
  expiry?: number;
}
export function getApisToCache(): Cache[] {
  return [
    { url: API.SECURITY_QUESTIONS },
    { url: API.GET_DRIVER_STATS_V2 },
    { url: API.GET_DRIVER_TRIPS_V2 },
    { url: API.GET_TRIP_LIST_V2 },
    { url: API.GET_DRIVER_LIST_V2 },
    { url: API.GET_UNIQUE_DRIVERS_FOR_A_USER },
    { url: API.EVENT_DETAILS },
    { url: API.TRIP_DETAILS },

    { url: API.GET_FLEET_STATS },
    { url: API.GET_DRIVER_LIST },
    { url: API.GET_FLEET_EVENT_TREND },
    { url: API.GET_DRIVER_EVENT_TREND },
    { url: API.GET_SEVERE_VIOLATIONS },
    { url: API.GET_DVR_REQUESTS },
    { url: API.GET_EDVR_REQUESTS },
    { url: API.GET_ANNOUNCEMENTS },
    { url: API.GET_DRIVER_IMAGES },
    { url: API.GET_REGISTERED_DRIVERS },
    { url: API.GET_FLEET_ASSETS },
    { url: API.GET_VIOLATIONS },
    { url: API.GEO_JSON },
    { url: API.GET_EXTERNAL_EVENTS },
    { url: API.GET_ANNOUNCEMENTS },
    { url: API.GET_FLEET_CONFIGURATION },
    { url: API.REVERSE_GEOCODE },
    { url: API.GET_COACHING_INCIDENTS },
    { url: API.GET_CHALLENGED_INCIDENTS },
    { url: API.GET_FLEET_ASSETS },
    { url: API.EXPORT_ASSETS },
    { url: API.GET_IMPROVED_DRIVERS },
    { url: API.GET_FLEET_DEVICE_STATS },
    { url: API.GET_ARCHIVED_REPORTS },
    { url: API.GET_ARCHIVED_REPORTS_AGGREGATE },
    { url: API.GET_DRIVER_PERMISSIONS },
    { url: API.CHECK_DVR_AVAIBILITY },

    { url: API.LIST_COACHING_SESSION },
    { url: API.COACHING_RECPMMENDATIONS },

    { url: API.ASSET_PLANS },
    { url: API.DEVICE_MODEL_CONFIG },
    { url: API.GET_FLEET_CUSTOM_EVENT_CONFIGURATION },
    { url: API.GET_FLEET_EVENTS_CUSTOM_EVENTS },
    { url: API.GET_COACHING_CONFIG },
    { url: API.GET_EVENT_STREAK },

    { url: API.EULA_CONSENT_V2 },
    { url: API.GET_CRITICAL_EVENT },
    { url: API.GET_INSTALLATION_REPORT },
    { url: API.GET_FLEET_HEALTH_Agg },
    { url: API.GET_CAMERA_EVENT_Agg },
    { url: API.GET_CAMERA_ONLINE_STATUS_LIST },
    { url: API.GET_TOTAL_DEVCIES },
    { url: API.GET_TOTAL_CURRENT_ISSUES },
    { url: API.CLIENT_CONFIG },
    { url: API.GET_FLEET_DETAILS },
    { url: API.SCHEDULED_REPORTS },
    { url: API.NOTIFICATION_CONFIGURATION },

    { url: API.GET_ATTRIBUTES_V2 },
    { url: API.GET_ALL_TAGLIST },
    { url: API.GET_ENTITY_DETAILS },
    { url: API.GET_AI_RECOGNIZED_EVENTS },

    { url: API.LIST_GEOFENCE },
    { url: API.LIST_GEOFENCE_SETTING },
    { url: API.LIST_GEOFENCE_ACTIVITY },
    { url: API.LIST_GEOFENCE_LOCATION },
    { url: API.GET_FLEET_BRANDING_CONFIG },
  ];
}

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  public isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return control && control.invalid && (control.dirty || control.touched || isSubmitted);
  }
}

export const ROUTER_LINK_PAGE_NAME_MAP = {
  '/home': 'Home',
  '/safety-events': 'Safety Events',
  '/trips': 'Trips',
  '/live-view': 'Live View',
  '/drivers': 'Drivers',
  '/drivers/coaching-session': 'Coaching Session',
  '/reports': 'Reports',
  '/reports/export-history': 'Export History',
  '/reports/fleet-safety': 'Fleet Safety Report',
  '/reports/coaching-session': 'Coaching Session Report',
  '/assets': 'Assets',
  '/configurations': 'Configurations',
  '/trip-details': 'Trip Details',
  '/request-video': 'Request Video',
  '/coaching': 'Coaching',
  '/video-requests': 'Video Requests',
  '/challenges': 'Challenges',
  '/user-management': 'User Management',
  '/notifications-page': 'Notifications',
};

export const INTERNAL_ROUTES = [
  '/trip-details',
  '/request-video',
  '/drivers/coaching-session',
  '/drivers/edit-driver',
  '/drivers/driver-details',
  '/reports/coaching-effectiveness-driver',
  '/user-management/activity-log',
];

export const DEFAULT_DURATION_LIST: Duration[] = [
  // {
  //   title: 'Today',
  //   days: 1,
  // },
  // {
  //   title: 'Yesterday',
  //   days: 'yesterday',
  // },
  {
    title: 'Past 3 days',
    days: 3,
  },
  {
    title: 'Past 7 days',
    days: 7,
  },
  {
    title: 'Past 14 days',
    days: 14,
  },
  {
    title: 'Past 30 days',
    days: 30,
  },
  {
    title: 'Custom',
    days: 'custom',
  },
];

export const DEFAULT_DATA_RETENTION_DAYS = 180;
export const MAX_SELECTABLE_DATE_RANGE_IN_DAYS = 30;

export const REPORTS_DURATION_LIST = [
  {
    title: 'Past 30 days',
    days: 30,
  },
  {
    title: 'Past 90 days',
    days: 30 * 3,
  },
];

const localTimeZone = {
  key: 'Local',
  value: 'Local',
  timezoneOffset: 0,
  customLabel: 'Local',
};

export const SUPPORTED_TIMEZONES = [
  ...moment.tz.names().map((zone) => {
    const utcOffsetMinutes = moment.tz(zone).utcOffset();
    const hours = Math.abs(Math.floor(utcOffsetMinutes / 60));
    const minutes = Math.abs(utcOffsetMinutes % 60);
    const sign = utcOffsetMinutes >= 0 ? '+' : '-';

    const formattedUtcOffset = `UTC ${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    const customLabel = `(${formattedUtcOffset}) ${zone}`;
    return {
      key: zone,
      value: zone,
      utcOffset: formattedUtcOffset,
      timezoneOffset: utcOffsetMinutes,
      customLabel: customLabel,
    };
  }),
  localTimeZone,
];

export const SUPPORTED_METRIC_UNITS = ['Miles', 'Kilometers'];

export const SUPPORTED_DATE_FORMATS = [
  {
    key: 'MM/DD/YYYY HH:mm:ss',
    value: 'MM/DD/YYYY HH:mm:ss',
  },
  {
    key: 'DD/MM/YYYY HH:mm:ss',
    value: 'DD/MM/YYYY HH:mm:ss',
  },
  {
    key: 'YYYY/MM/DD HH:mm:ss',
    value: 'YYYY/MM/DD HH:mm:ss',
  },
];

export const READABLE_SUPPORTED_DATE_FORMATS = [
  {
    key: 'MM/DD/YYYY HH:mm:ss',
    value: 'MMM D, YYYY HH:mm:ss',
  },
  {
    key: 'DD/MM/YYYY HH:mm:ss',
    value: 'D MMM, YYYY HH:mm:ss',
  },
  {
    key: 'YYYY/MM/DD HH:mm:ss',
    value: 'YYYY, MMM D HH:mm:ss',
  },
];

export const SUPPORTED_LANGUAGES = [
  {
    key: 'en',
    value: 'English - United States',
    country: 'United States',
    languageName: 'English',
  },
  {
    key: 'es',
    value: 'Español - España',
    country: 'Spain',
    languageName: 'Spanish',
  },
  {
    key: 'fr',
    value: 'Français - La France',
    country: 'France',
    languageName: 'French',
  },
  {
    key: 'pt',
    value: 'Português - Portugal',
    country: 'Portugal',
    languageName: 'Portuguese',
  },
  {
    key: 'pt-BR',
    value: 'Portugues-Brasil',
    country: 'Brazil',
    languageName: 'Brazil',
  },
  {
    key: 'fr-CA',
    value: 'Français - Canada',
    country: 'Canada',
    languageName: 'French',
  },
  {
    key: 'en-GB',
    value: 'English - United Kingdom',
    country: 'United Kingdom',
    languageName: 'English',
  },
  {
    key: 'es-419',
    value: 'Español - América Latina',
    country: 'Latin America',
    languageName: 'Spanish',
  },
  {
    key: 'ja',
    value: '日本語 - 日本',
    country: 'Japan',
    languageName: 'Japanese',
  },
  {
    key: 'ko',
    value: '한국어 (대한민국)',
    country: 'South Korea',
    languageName: 'Korean',
  },
] as const;

export const GEOTAB_LANGUAGES_MAPPING: { [geotabLanguageCode in string]: (typeof SUPPORTED_LANGUAGES)[number]['key'] } = {
  en: 'en',
  fr: 'fr-CA',
  'fr-FR': 'fr',
  ja: 'ja',
  'ko-KR': 'ko',
  'pt-BR': 'pt-BR',
  'pt-PT': 'pt',
  es: 'es',
};

export const CSV_MIME_TYPE = [
  'text/plain',
  'text/x-csv',
  'application/vnd.ms-excel',
  'application/csv',
  'application/x-csv',
  'text/csv',
  'text/comma-separated-values',
  'text/x-comma-separated-values',
  'text/tab-separated-values',
];

export const PASSWORD_PATTERN = '^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{9,99}$';

export const LOGIN_INFO = 'LOGIN_INFO';

export const AUTH_INFO = 'AUTH_INFO';

export const FLEETMANAGER = 'fleetmanager';

export const TRIP_DETAILS_VIEW_TYPE_KEY = 'trip-details-view-type';

export const DEFAULT_TRIP_VIEW = TripDetailsViewType.table;

export const EVENT_MODAL_VIEW_TYPE_KEY = 'event-modal-view-type';

export const SHOW_EXTERNAL_EVENTS = true;

export const TIMEZONE_KEY = 'current-timezone';

export const DEFAULT_TIMEZONE = 'Local';

export const METRIC_UNIT_KEY = 'current-metric-unit';

export const DRIVER_CENTRIC = 'driver-centric';

export const DEFAULT_METRIC_UNIT = 'Miles';

export const DATE_FORMAT_KEY = 'current-date-format';

export const DATE_FORMAT_READABLE_KEY = 'date-format-readable';

export const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY HH:mm';

export const FLEET_KEY = 'current-fleet';

export const LANGUAGE_KEY = 'current-language';

export const DEFAULT_LANGUAGE = 'en';

export const USER_OVERRIDDEN_SETTINGS_KEY = 'user-overridden-settings';

export const THEME_KEY = 'current-theme';

export const DEFAULT_THEME = 'light';

export const MAX_DATE_RANGE_IN_MONTHS = 6;

export const MAX_SELECTABLE_DATE_RANGE_IN_MONTHS = 1;

export const COOKIES_CONSENT_KEY = 'current-cookies-consent';

export const DEFAULT_COOKIES_CONSENT = null;

/* eslint-disable-next-line max-len */

export const NAME_REGEX = /^(?=.*\p{L})[\p{L}\p{M} '_,.-]+$/u;

export const USER_MANAGEMENT_TABLE_COLUMNS = [
  'email',
  'name',
  'role',
  'accountStatus',
  'tags',
  'creationDate',
  // 'lastUpdatedOn',
  'lastUpdatedBy',
  'actions',
];

export const USER_COUNTRY_KEY = 'user-country';
export const LANDING_PAGE_LANGUAGE_KEY = 'landing-page-language';

export const AWS_COGNITO_REGION_KEY = 'aws-cognito-region';

export const US_CENTER_LAT_LNG = {
  latitude: 40.25,
  longitude: -74.5,
};

export const MAP_OPTIONS = {
  zoom: 3,
  coordinates: { lat: US_CENTER_LAT_LNG.latitude, lng: US_CENTER_LAT_LNG.longitude },
  zoomControl: false,
};

export const MAP_MARKER_ICON_OPTIONS = {
  iconUrl: 'assets/leaflet/marker-icon.png',
  iconSize: [20, 30],
  iconAnchor: [10, 30],
};

export const MAP_CIRCLE_ICON_OPTIONS = {
  color: '#00ff00',
  fill: true,
  fillOpacity: 1,
  radius: 5,
};

export const EVENTS_CONFIG = {
  'Traffic-Speed-Violated': {
    label: 'Speed Limit Violation',
    color: '#00bfff',
    showHighlights: true,
    showIncidentTrend: true,
    showIncidentSummary: true,
    showInFilter: true,
    positiveRecognistion: 'Speed Sign Compliant',
    childConfigs: {
      postedSpeed: {
        label: 'Posted Speed',
      },
      MaxSpeedExceeded: {
        label: 'Maximum Speed',
        shortLabel: 'Max. Speed',
      },
    },
  },
  Cornering: {
    label: 'Harsh Cornering',
    color: '#8000ff',
    showHighlights: true,
    showIncidentTrend: true,
    showIncidentSummary: true,
    showInFilter: true,
    positiveRecognistion: 'Safe Driver',
  },
  'Traffic-STOP-Sign-Violated': {
    label: 'Stop Sign Violation',
    color: '#b5651d',
    showHighlights: true,
    showIncidentTrend: true,
    showIncidentSummary: true,
    showInFilter: true,
    positiveRecognistion: 'Stop Sign Compliant',
  },
  'Harsh-Braking': {
    label: 'Harsh Braking',
    color: '#ff00ff',
    showHighlights: true,
    showIncidentTrend: true,
    showIncidentSummary: true,
    showInFilter: true,
    positiveRecognistion: 'Safe Driver',
  },
  'Tail-Gating-Detected': {
    label: 'Tailgating',
    color: '#F6BE00',
    showHighlights: true,
    showIncidentTrend: true,
    showIncidentSummary: true,
    showInFilter: true,
    positiveRecognistion: 'Defensive Driver',
  },
  'Harsh-Acceleration': {
    label: 'Harsh Acceleration',
    color: '#228B22',
    showHighlights: true,
    showIncidentTrend: true,
    showIncidentSummary: true,
    showInFilter: true,
    positiveRecognistion: 'Responsible Driver',
  },
  'Lane-Drift-Found': {
    label: 'Lane Drift',
    color: '#FFA500',
    showHighlights: false,
    showIncidentTrend: false,
    showIncidentSummary: false,
    showInFilter: false,
    positiveRecognistion: '-',
  },
  'Distracted-Driving': {
    label: 'Distracted Driving',
    color: '#e65000',
    showHighlights: true,
    showIncidentTrend: true,
    showIncidentSummary: true,
    isDriverFacing: true,
    showInFilter: true,
    positiveRecognistion: 'Attentive Driver',
    childConfigs: {
      distractedDriving: {
        label: 'Head Pose Deviation',
      },
      'Lizard-Eye-Distracted-Driving': {
        label: 'Gaze Down Distraction',
        shortLabel: 'Gaze Down',
      },
      'Smoking-Distracted-Driving': {
        label: 'Smoking Distraction',
        shortLabel: 'Smoking',
      },
      'Drinking-Distracted-Driving': {
        label: 'Drinking Distraction',
        shortLabel: 'Drinking',
      },
      'Texting-Distracted-Driving': {
        label: 'Texting Distraction',
        shortLabel: 'Texting',
      },
      'Cellphone-Distracted-Driving': {
        label: 'Cellphone Distraction',
        shortLabel: 'Cellphone',
      },
    },
  },
  'Forward-Collision-Warning': {
    label: 'Forward Collision Warning',
    color: '#000075',
    showHighlights: true,
    showIncidentTrend: true,
    showIncidentSummary: true,
    showInFilter: true,
    positiveRecognistion: 'Cautious Driver',
  },
  'Drowsy-Driving-Detected': {
    label: 'Drowsy Driving',
    color: '#d45087',
    showHighlights: true,
    showIncidentTrend: true,
    showIncidentSummary: true,
    isDriverFacing: true,
    showInFilter: true,
    positiveRecognistion: 'Alert Driver',
    childConfigs: {
      'Drowsy-Driving-Detected': {
        label: 'Drowsy',
        shortLabel: 'Drowsy',
      },
      'Driver-Fatigue-Detected': {
        label: 'Fatigue',
        shortLabel: 'Fatigue',
      },
    },
  },
  'Roll-Over-Detected': {
    label: 'Rollover Detection',
    color: '#f95d6a',
    showHighlights: false,
    showIncidentTrend: true,
    showIncidentSummary: true,
    showInFilter: true,
    positiveRecognistion: '-',
  },
  'Unbuckled-Seat-Belt': {
    label: 'Seatbelt Violation',
    color: '#003f5c',
    showHighlights: true,
    showIncidentTrend: true,
    showInFilter: true,
    isDriverFacing: true,
    positiveRecognistion: 'Seatbelt Compliant',
  },
  'Traffic-Light-Violated': {
    label: 'Traffic Light Violation',
    color: '#BA4A00',
    showHighlights: true,
    showIncidentTrend: true,
    showInFilter: true,
    showIncidentSummary: true,
    positiveRecognistion: 'Traffic Light Compliant',
  },
};

export const COLLISION_EVENTS = {
  PotentialCrash: {
    label: 'High-G Event',
    color: '#f95d6a',
    showHighlights: false,
    showIncidentTrend: false,
    showInFilter: false,
    showIncidentSummary: false,
    isCollisionEvent: true,
  },
  'Roll-Over-Detected': {
    ...EVENTS_CONFIG['Roll-Over-Detected'],
    isCollisionEvent: true,
  },
};

export const CONFIGS_WITH_SUBCONFIGS = {
  'Distracted-Driving': [
    'Lizard-Eye-Distracted-Driving',
    'Smoking-Distracted-Driving',
    'Drinking-Distracted-Driving',
    'Cellphone-Distracted-Driving',
    'Texting-Distracted-Driving',
  ],
  'Traffic-Speed-Violated': ['MaxSpeedExceeded'],
  'Drowsy-Driving-Detected': ['Driver-Fatigue-Detected'],
};

export const CUSTOMER_WITH_USER_PERMISSION_FEATURE = ['orbcomm'];

export const SAFETY_EVENT_COLUMN_OPTION = ['select', 'video', 'eventType', 'assetId', 'driverId', 'severity', 'timestamp', 'actions'];
export const SAFETY_EVENT_PAGE_OPTION = [10, 20, 30, 50];

export const EVENT_SEVERITY_TYPE_LIST = {
  'Traffic-Speed-Violated': {
    severity: (event) => event.speedingValue,
    metricKey: 'speedingValue',
    high: 32,
    low: 16,
  },
  MaxSpeedExceeded: {
    severity: (event) => event.exceededSpeedingValue,
    metricKey: 'exceededSpeedingValue',
    high: 32,
    low: 16,
  },
  'Tail-Gating-Detected': {
    severity: (event) => event.tailgatingMetricValue,
    metricKey: 'tailgatingMetricValue',
    high: 1,
    low: 2,
  },
  'Traffic-STOP-Sign-Violated': {
    severity: (event) => event.lowestSpeedKmph,
    metricKey: 'lowestSpeedKmph',
    high: 24,
    low: 16,
  },
  'Harsh-Acceleration': {
    severity: (event) => event.harshAccelerationValue,
    metricKey: 'harshAccelerationValue',
    high: 3576,
    low: 2682,
  },
  'Harsh-Braking': {
    severity: (event) => event.harshBrakingAccelerationValue,
    metricKey: 'harshBrakingAccelerationValue',
    high: 3576,
    low: 2682,
  },
  Cornering: {
    severity: (event) => event.corneringAccelerationValue,
    metricKey: 'corneringAccelerationValue',
    high: 3576,
    low: 2682,
  },
  'Distracted-Driving': {
    severity: (event) => event.severity,
    metricKey: 'severity',
    high: 80,
    low: 60,
  },
  'Forward-Collision-Warning': {
    severity: (event) => event.forwardCollisionMetricValue,
    metricKey: 'forwardCollisionMetricValue',
    high: 1,
    low: 2,
  },
  'Drowsy-Driving-Detected': {
    severity: (event) => event.severity,
    metricKey: 'severity',
    high: 80,
    low: 60,
  },
};

export const Durations = [
  {
    title: 'Past 3 Days',
    days: 3,
  },
  {
    title: 'Past Week',
    days: 7,
  },
  {
    title: 'Past Month',
    days: 30,
  },
  {
    title: 'Past 3 Months',
    days: 90,
  },
];

export const IMAGE_LIST = [
  {
    imageUrl: 'assets/common/fr-images/image1.png',
    isValid: true,
  },
  {
    imageUrl: 'assets/common/fr-images/image2.png',
    isValid: true,
  },
  {
    imageUrl: 'assets/common/fr-images/image3.png',
    isValid: true,
  },
  {
    imageUrl: 'assets/common/fr-images/image4.png',
    isValid: true,
  },
  {
    imageUrl: 'assets/common/fr-images/image5.png',
    isValid: true,
  },
  {
    imageUrl: 'assets/common/fr-images/image6.png',
    isValid: false,
  },
  {
    imageUrl: 'assets/common/fr-images/image7.png',
    isValid: false,
  },
  {
    imageUrl: 'assets/common/fr-images/image8.png',
    isValid: false,
  },
];

export const DVR_STATUS_LIST = {
  PENDING_FOR_REVIEW: {
    type: 'PENDING_FOR_REVIEW',
    description: '-',
  },
  PROCESSING: {
    type: 'PROCESSING',
    description: 'Video request is being processed and will be available soon.',
  },
  FAILED: {
    type: 'FAILED',
    description: 'Attempt to get the video failed. The device will retry.',
  },
  UNAVAILABLE: {
    type: 'UNAVAILABLE',
    description: 'Video no longer exists on the device - it has been overwritten.',
  },
  CANCELED: {
    type: 'CANCELED',
    description: 'Video request cannot be serviced. Contact your service provider.',
  },
  FINISHED: {
    type: 'FINISHED',
    description: 'Video has been fetched and is ready for viewing.',
  },
};

export const DVR_STATUS_TIMELINE_MAPPING = {
  NotificationInitiated: {
    description: 'Request Created',
    type: 'success',
  },
  NotificationReceived: {
    description: 'Notification received by the camera',
    type: 'success',
  },
  NotificationCancelled: {
    description: 'Notification sending cancelled',
    type: 'error',
  },
  MediaUnavailable: {
    description: 'Video unavailable',
    type: 'error',
  },
  MediaGenerationStarted: {
    description: 'Video processing started',
    type: 'success',
  },
  MediaGenerationFailed: {
    description: 'Video processing failed',
    type: 'error',
  },
  MediaGenerationSuccessful: {
    description: 'Video processing successful',
    type: 'success',
  },
  MediaUploadStarted: {
    description: 'Video upload started',
    type: 'success',
  },
  MediaUploadFailed: {
    description: 'Video upload failed',
    type: 'error',
  },
  MediaUploadCompleted: {
    description: 'Video upload completed, ready to view now',
    type: 'success',
  },
  CustomStatusWaitingForNextUpdate: {
    description: 'Waiting for next status update',
    type: 'warn',
  },
};

export const EVENT_TAG_LIST = [
  {
    value: 'CELLPHONE',
    text: 'Cellphone',
  },
  {
    value: 'COLLISION',
    text: 'Collision',
  },
  {
    value: 'EATING_OR_DRINKING',
    text: 'Drinking/Eating',
  },
  {
    value: 'NO_SEATBELT',
    text: 'No seatbelt',
  },
  {
    value: 'SMOKING',
    text: 'Smoking',
  },
  {
    value: 'OTHER',
    text: 'Other',
  },
];

export const EVENT_TAG_KEYS = {
  CELLPHONE: 'Cellphone',
  COLLISION: 'Collision',
  EATING_OR_DRINKING: 'Drinking/Eating',
  NO_SEATBELT: 'No seatbelt',
  SMOKING: 'Smoking',
  OTHER: 'Other',
};

export const MEDIA_SOURCES_ENUM = {
  ROAD_FACING: 'Road facing',
  DRIVER_FACING: 'Driver facing',
  SIDE_BY_SIDE: 'Side-by-side',
  PIP_ROAD_MAJOR: 'PiP Road major',
  PIP_DRIVER_MAJOR: 'PiP Driver major',
};

// DVR
export const MININUM_DVR_DURATION_IN_SECONDS = 10;
export const MAXIMUM_DVR_DURATION_IN_SECONDS = 180;
export const SLIDER_TICK_STEP = 10;
export const DVR_RESOLUTION_OPTIONS = [
  {
    value: '320x180',
    text: '320x180',
  },
  {
    value: '640x360',
    text: '640x360',
  },
  {
    value: '1280x720',
    text: '1280x720',
  },
  {
    value: '1920x1080',
    text: '1920x1080',
  },
];
export const DVR_REQUEST_LEGENDS = [
  {
    color: '#be3d3b',
    label: 'High-G Event',
  },
  {
    color: '#ff00ff',
    label: 'Harsh Braking',
  },
  {
    color: '#303030',
    label: 'Stationary',
  },
  {
    color: '#ff8c00',
    label: 'Video Request',
  },
  {
    color: '#005dbb',
    label: 'Safety Events',
  },
];
export const DVR_DURATION_OPTIONS = [
  {
    groupName: 'Standard Video',
    groupId: 'STANDARD',
    options: [
      {
        value: 60,
        text: '1',
        unit: 'minute',
      },
      {
        value: 120,
        text: '2',
        unit: 'minutes',
      },
      {
        value: 180,
        text: '3',
        unit: 'minutes',
      },
    ],
    showDivider: true,
  },
  {
    groupName: 'Time-lapse Video',
    groupId: 'TIMELAPSE',
    options: [
      {
        value: 300,
        text: '5',
        unit: 'minutes',
      },
      {
        value: 600,
        text: '10',
        unit: 'minutes',
      },
      {
        value: 900,
        text: '15',
        unit: 'minutes',
      },
      {
        value: 1800,
        text: '30',
        unit: 'minutes',
      },
      {
        value: 2700,
        text: '45',
        unit: 'minutes',
      },
      {
        value: 3600,
        text: '60',
        unit: 'minutes',
      },
    ],
  },
];

export const TIMELAPSE_DVR_DURATION_OPTIONS = [
  {
    value: 60,
    text: '1',
    unit: 'minute',
    disabled: false,
  },
  {
    value: 120,
    text: '2',
    unit: 'minutes',
    disabled: false,
  },
  {
    value: 180,
    text: '3',
    unit: 'minutes',
    disabled: false,
  },
];
export const DEFAULT_DVR_DURATION = 60;
export const DEFAULT_DVR_RESOLUTION = '1280x720';
export const DEFAULT_VIDEO_FORMAT = 'sideBySide';
export const DEFAULT_PIP_FORMAT = 'road';

export const VIDEO_FORMATS = [
  {
    value: 'road',
    text: 'Road',
  },
  {
    value: 'driver',
    text: 'Driver',
  },
  {
    value: 'sideBySide',
    text: 'Side-by-side',
  },
  {
    value: 'pictureInPictureRoadSide',
    text: 'Picture-in-picture (Road Side)',
  },
  {
    value: 'pictureInPictureDriverSide',
    text: 'Picture-in-picture (Driver Side)',
  },
  {
    value: 'separate',
    text: 'Road + Driver',
  },
];

export const DVR_TYPE_LIST = [
  {
    label: 'DVR',
    value: 'dvr',
  },
  {
    label: 'Time-lapse DVR',
    value: 'timelapseDvr',
  },
];

export const VIDEO_TYPE_LIST = [
  {
    label: 'Video Requests',
    value: 'dvr',
  },
  {
    label: 'Event On-Demand',
    value: 'edvr',
  },
];

export const EXCLUDED_EVENTS = [
  // Standard
  'Roll-Over-Detected',
  'externalEvents',

  // Beta
  'Smoking-Distracted-Driving',
  'Drinking-Distracted-Driving',
  'Texting-Distracted-Driving',
  'Driver-Fatigue-Detected',
  'Traffic-Light-Violated',
];

export const INCIDENT_TYPE_LIST = [
  {
    label: 'Distracted Driving',
    value: 'Distracted-Driving',
    type: 'INCIDENT',
    childConfigs: {
      distractedDriving: {
        label: 'Head Pose Deviation',
      },
      'Lizard-Eye-Distracted-Driving': {
        label: 'Gaze Down Distraction',
      },
      'Smoking-Distracted-Driving': {
        label: 'Smoking Distraction',
      },
      'Drinking-Distracted-Driving': {
        label: 'Drinking Distraction',
      },
      'Texting-Distracted-Driving': {
        label: 'Texting Distraction',
      },
      'Cellphone-Distracted-Driving': {
        label: 'Cellphone Distraction',
      },
    },
  },
  {
    label: 'Drowsy Driving',
    value: 'Drowsy-Driving-Detected',
    type: 'INCIDENT',
    childConfigs: {
      'Drowsy-Driving-Detected': {
        label: 'Drowsy',
      },
      'Driver-Fatigue-Detected': {
        label: 'Fatigue',
      },
    },
  },
  {
    label: 'Forward Collision Warning',
    value: 'Forward-Collision-Warning',
    type: 'INCIDENT',
  },
  {
    label: 'Harsh Acceleration',
    value: 'Harsh-Acceleration',
    type: 'INCIDENT',
  },
  {
    label: 'Harsh Braking',
    value: 'Harsh-Braking',
    type: 'INCIDENT',
  },
  {
    label: 'Harsh Cornering',
    value: 'Cornering',
    type: 'INCIDENT',
  },
  {
    label: 'Lane Drift',
    value: 'Lane-Drift-Found',
    type: 'INCIDENT',
  },
  {
    label: 'Speed Limit Violation',
    value: 'Traffic-Speed-Violated',
    type: 'INCIDENT',
    childConfigs: {
      postedSpeed: {
        label: 'Posted Speed',
      },
      MaxSpeedExceeded: {
        label: 'Maximum Speed',
      },
    },
  },
  {
    label: 'Stop Sign Violation',
    value: 'Traffic-STOP-Sign-Violated',
    type: 'INCIDENT',
  },
  {
    label: 'Tailgating',
    value: 'Tail-Gating-Detected',
    type: 'INCIDENT',
  },
  {
    label: 'Rollover Detection',
    value: 'Roll-Over-Detected',
    type: 'INCIDENT',
  },
  {
    label: 'Seatbelt Violation',
    value: 'Unbuckled-Seat-Belt',
    type: 'INCIDENT',
  },
  {
    label: 'Traffic Light Violation',
    value: 'Traffic-Light-Violated',
    type: 'INCIDENT',
  },
  {
    label: 'DVR',
    value: 'dvr',
    type: 'DVR',
  },
];

export enum CoachingStatus {
  ALL = 'ALL',
  BOTH = 'BOTH',
  PENDING = 'PENDING',
  COACHED = 'COACHED',
  EXPIRED = 'EXPIRED',
}

export const COACHING_STATUS_LIST = [
  {
    label: 'All',
    value: 'all',
    status: 'all',
  },
  {
    label: 'Pending',
    value: false,
    status: 'pending',
  },
  {
    label: 'Completed',
    value: true,
    status: 'completed',
  },
];

export const IN_PERSON_COACHING_STATUS_LIST_ENABLED_SELF_COACHING = [
  // {
  //   label: 'All',
  //   value: 'all',
  //   status: 'all',
  // },
  {
    label: 'Pending',
    value: false,
    status: 'pending',
  },
  {
    label: 'Completed',
    value: true,
    status: 'completed',
  },
];

export const CLOSED_COACHING_STATUS_LIST_SELF_COACHING = [
  // {
  //   label: 'Both',
  //   value: CoachingStatus.BOTH,
  // },
  {
    label: 'Completed',
    value: CoachingStatus.COACHED,
  },
  {
    label: 'Expired',
    value: CoachingStatus.EXPIRED,
  },
];

export const COACHING_STATUS_LIST_SELF_COACHING = [
  // {
  //   label: 'Both',
  //   value: CoachingStatus.BOTH,
  // },
  {
    label: 'Pending',
    value: CoachingStatus.PENDING,
  },
  {
    label: 'Expired',
    value: CoachingStatus.EXPIRED,
  },
];

export enum CoachingType {
  IN_PERSON_ESCALATION = 'IN_PERSON_ESCALATION',
  SELF = 'SELF',
}

export const COACHING_TYPE_LIST = [
  {
    label: 'Self',
    value: CoachingType.SELF,
  },
  {
    label: 'In-person',
    value: CoachingType.IN_PERSON_ESCALATION,
  },
];

export const CHALLENGED_STATUS_LIST = [
  {
    label: 'All',
    value: 'all',
  },
  {
    label: 'Pending', // status is 'raised'
    value: 'pending',
  },
  {
    label: 'Rejected',
    value: 'rejected',
  },
  {
    label: 'Accepted',
    value: 'accepted',
  },
];

export const VIDEO_REQUEST_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];
export const COACHING_PANEL_PAGE_SIZE = 5;
export const CHALLENEGE_LIST_PAGE_SIZE = 5;
export const PANIC_BUTTON_LIST_PAGE_SIZE = 5;
export const TRIP_DETAILS_EVENT_LIST_PAGE_SIZE = 10;
export const LIVEVIEW_ASSET_LIST_PAGE_SIZE = 10;

// TIMELAPSE DVR
export const MININUM_TIMELAPSE_CAPTURE_INTERVAL = 0;
export const MAXIMUM_TIMELAPSE_CAPTURE_INTERVAL = 3600;
export const TIMELAPSE_CAPTURE_INTERVAL_TICK_STEP = 600;

export const MININUM_TIMELAPSE_DISPLAY_INTERVAL = 0.1;
export const MAXIMUM_TIMELAPSE_DISPLAY_INTERVAL = 10;

export const ASSET_MAP_MARKER_OPTIONS = {
  iconSize: [40, 50],
  popupAnchor: [0, -50],
  iconAnchor: [20, 50],
};

// LIVESTREAM
export const LIVESTREAM_VIDEO_RESOLUTIONS = ['320x180', '640x360', '1280x720'];
export const LIVESTREAM_DEFAULT_VIDEO_RESOLUTION = '320x180';
export const LIVESTREAM_DEFAULT_VIDEO_TYPE = 'road';
export const LIVESTREAM_DEFAULT_PIP_MAIN_FRAME = 'road';
export const LIVESTREAM_MODAL_CONFIG = {
  panelClass: ['livestream-modal', 'mobile-modal'],
  backdropClass: 'livestream-modal-backdrop',
  position: { bottom: '0px', right: '0px' },
  disableClose: true,
};

export const BREAKPOINTS_XSMALL = [Breakpoints.XSmall];
export const BREAKPOINTS_SMALL = [Breakpoints.Small, Breakpoints.XSmall];
export const BREAKPOINTS_MEDIUM = [Breakpoints.Medium, Breakpoints.Small, Breakpoints.XSmall, Breakpoints.Handset];
export const BREAKPOINTS_LARGE = [Breakpoints.Large, Breakpoints.XLarge];
export const BREAKPOINTS_LANDSCAPE = [Breakpoints.HandsetLandscape];
export const BREAKPOINTS_PORTRAIT = [Breakpoints.HandsetPortrait];

export const AWS_DOMAIN = 'amazonaws.com';
export const ARCGIS_DOMAIN = 'geocode.arcgis.com';

export const LOGOUT_TIME_DURATION_IN_SECS = 10;

export const AUTO_TAGS_LIST = {
  CELLPHONE: 'Cellphone',
  MAX_SPEED: 'Max. Speed',
  LIZARD_EYE: 'Gaze down',
  SMOKING: 'Smoking',
  DRINKING: 'Drinking',
  TEXTING: 'Texting',
  WORK_ZONE: 'Workzone (BETA)',
  FATIGUE: 'Fatigue',
  DROWZY: 'Drowsy',
};

export const MAPBOX_DEFAULT_LAYER = 'streets-v12';

export const MILES_TO_KILOMETERS_CONVERSION = 1.609;

export const DEFAULT_FONT_FAMILY = `Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue', sans-serif`;
export const DEFAULT_LIGHT_THEME_TEXT_COLOR = '#333333';
export const DEFAULT_DARK_THEME_TEXT_COLOR = '#EEEEEE';
export const DEFAULT_LIGHT_THEME_BORDER_COLOR = '#EEEEEE';
export const DEFAULT_DARK_THEME_BORDER_COLOR = '#666666';

export const INCIDENT_SUMMARY_CHART_OPTIONS = {
  type: 'pie',
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          pointStyle: 'circle',
          usePointStyle: true,
          color: DEFAULT_LIGHT_THEME_TEXT_COLOR,
          font: {
            family: DEFAULT_FONT_FAMILY,
          },
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            return datasets[0].data.map((data, i) => ({
              text: `${chart.data.labels[i]} ${data} %`,
              fillStyle: datasets[0].backgroundColor[i],
              index: i,
              fontColor: DEFAULT_LIGHT_THEME_TEXT_COLOR,
            }));
          },
        },
      },
      tooltip: {
        boxWidth: 10,
        boxHeight: 10,
        boxPadding: 4,
        cornerRadius: 4,
        usePointStyle: true,
        bodyFont: {
          family: DEFAULT_FONT_FAMILY,
        },
        callbacks: {
          label: (tooltipItem) => {
            return `${tooltipItem.dataset.label}: ${tooltipItem.formattedValue} %`;
          },
        },
      },
    },
  },
};

export const INCIDENT_TREND_CHART_OPTIONS = {
  type: 'line',
  options: {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        offset: true,
        grid: {
          display: false,
        },
        ticks: {
          font: {
            family: DEFAULT_FONT_FAMILY,
          },
        },
      },
      y: {
        title: {
          display: true,
          text: 'Incidents / 100 (mi/km)',
          font: {
            family: DEFAULT_FONT_FAMILY,
          },
        },
        beginAtZero: true,
        border: {
          display: false,
        },
        grid: {
          color: DEFAULT_LIGHT_THEME_BORDER_COLOR,
        },
        ticks: {
          color: DEFAULT_LIGHT_THEME_BORDER_COLOR,
          font: {
            family: DEFAULT_FONT_FAMILY,
          },
          precision: 0,
        },
      },
    },
    plugins: {
      datalabels: {
        display: false,
      },
      spanGaps: true,
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          pointStyle: 'circle',
          usePointStyle: true,
          color: DEFAULT_LIGHT_THEME_TEXT_COLOR,
          font: {
            family: DEFAULT_FONT_FAMILY,
          },
        },
      },
      tooltip: {
        boxWidth: 10,
        boxHeight: 10,
        boxPadding: 4,
        cornerRadius: 4,
        usePointStyle: true,
        bodyFont: {
          family: DEFAULT_FONT_FAMILY,
        },
      },
    },
  },
};

export const CHART_OPTIONS_COACHING_EFFECTIVENESS_EVENT_WISE = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      title: {
        display: true,
        text: 'EVENTS SELECTED FOR COACHING',
        font: {
          family: DEFAULT_FONT_FAMILY,
          weight: 'medium',
        },
      },
      offset: true,
      grid: {
        display: false,
      },
      ticks: {
        font: {
          family: DEFAULT_FONT_FAMILY,
        },
      },
    },
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: 'NUMBER OF EVENTS',
        font: {
          family: DEFAULT_FONT_FAMILY,
          weight: 'medium',
        },
      },
      border: {
        display: false,
      },
      grid: {
        color: DEFAULT_LIGHT_THEME_BORDER_COLOR,
      },
      ticks: {
        color: DEFAULT_LIGHT_THEME_BORDER_COLOR,
        font: {
          family: DEFAULT_FONT_FAMILY,
        },
        precision: 0,
      },
    },
  },
  plugins: {
    tooltip: {
      usePointStyle: true,
      bodyFont: {
        family: DEFAULT_FONT_FAMILY,
      },
    },
    datalabels: {
      display: false,
    },
    legend: {
      display: false,
    },
  },
};

export const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      title: {
        display: true,
        text: 'TIME RANGE',
        font: {
          family: DEFAULT_FONT_FAMILY,
          weight: 'medium',
        },
      },
      offset: true,
      ticks: {
        font: {
          family: DEFAULT_FONT_FAMILY,
        },
        callback: function (value) {
          // Ensure the labels are displayed as multiline
          const label = this.getLabelForValue(value);
          return Array.isArray(label) ? label : [label];
        },
      },
    },
    y: {
      title: {
        display: true,
        text: 'Events per 100 Miles',
        font: {
          family: DEFAULT_FONT_FAMILY,
          weight: 'medium',
        },
      },
      beginAtZero: true,
      border: {
        display: false,
      },
      grid: {
        color: DEFAULT_LIGHT_THEME_BORDER_COLOR,
      },
      ticks: {
        color: DEFAULT_LIGHT_THEME_BORDER_COLOR,
        font: {
          family: DEFAULT_FONT_FAMILY,
        },
        precision: 0,
      },
    },
  },
  plugins: {
    annotation: {
      annotations: {
        type: 'line',
        mode: 'vertical',
        scaleID: 'x',
        borderColor: '#00B63E',
        borderWidth: 3,
        borderDash: [10, 7],
        label: {
          display: false,
          enabled: true,
          position: 'top',
          xAdjust: -25,
          yAdjust: -10,
          font: {
            family: DEFAULT_FONT_FAMILY,
          },
        },
      },
    },
    datalabels: {
      display: false,
    },
    legend: {
      display: false,
      position: 'bottom',
      labels: {
        boxWidth: 10,
        boxHeight: 10,
        pointStyle: 'circle',
        usePointStyle: true,
        color: DEFAULT_LIGHT_THEME_TEXT_COLOR,
        font: {
          family: DEFAULT_FONT_FAMILY,
        },
      },
    },
  },
};

export const REPORT_INCIDENT_SUMMARY_CHART_OPTIONS = {
  type: 'bar',
  options: {
    layout: {
      padding: {
        top: 30,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        offset: true,
        grid: {
          display: false,
        },
        ticks: {
          font: {
            family: DEFAULT_FONT_FAMILY,
          },
        },
      },
      y: {
        title: {
          display: true,
          text: 'Incidents Count',
          font: {
            family: DEFAULT_FONT_FAMILY,
          },
        },
        beginAtZero: true,
        border: {
          display: false,
        },
        grid: {
          color: DEFAULT_LIGHT_THEME_BORDER_COLOR,
        },
        ticks: {
          color: DEFAULT_LIGHT_THEME_BORDER_COLOR,
          font: {
            family: DEFAULT_FONT_FAMILY,
          },
        },
      },
    },
    plugins: {
      datalabels: {
        anchor: 'end',
        align: 'top',
        formatter: (_val, context) => `${context.dataset.diffPercentage[context.dataIndex]}%`,
        color: DEFAULT_LIGHT_THEME_TEXT_COLOR,
      },
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          pointStyle: 'circle',
          usePointStyle: true,
          color: DEFAULT_LIGHT_THEME_TEXT_COLOR,
          font: {
            family: DEFAULT_FONT_FAMILY,
          },
        },
      },
      tooltip: {
        boxWidth: 10,
        boxHeight: 10,
        boxPadding: 4,
        cornerRadius: 4,
        usePointStyle: true,
        bodyFont: {
          family: DEFAULT_FONT_FAMILY,
        },
      },
    },
  },
};

export const EVENT_STREAK_CHART_OPTIONS = {
  type: 'bar',
  options: {
    responsive: true,
    indexAxis: 'y',
    scales: {
      x: {
        title: {
          display: true,
          text: 'Miles',
          font: {
            family: DEFAULT_FONT_FAMILY,
            size: 13,
            weight: 'bold',
          },
        },
        offset: false,
        ticks: {
          font: {
            family: DEFAULT_FONT_FAMILY,
          },
        },
      },
      y: {
        title: {
          display: true,
          text: 'Event Catagory',
          font: {
            family: DEFAULT_FONT_FAMILY,
            size: 13,
            weight: 'bold',
          },
        },
        beginAtZero: true,
        border: {
          display: false,
        },
        grid: {
          color: DEFAULT_LIGHT_THEME_BORDER_COLOR,
        },
        ticks: {
          color: DEFAULT_LIGHT_THEME_TEXT_COLOR,
          font: {
            family: DEFAULT_FONT_FAMILY,
          },
          precision: 0,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  },
};

export const SENSOR_PROFILE_CHART_ANNOTATION = {
  type: 'line',
  borderColor: 'red',
  borderDash: [10, 10],
  borderDashOffset: 0,
  borderWidth: 2,
};

export const SENSOR_PROFILE_CHART_OPTIONS = {
  type: 'line',
  options: {
    responsive: true,
    spanGaps: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        offset: true,
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
      },
      y: {
        title: {
          display: true,
          text: 'Acceleration (g)',
          font: {
            family: DEFAULT_FONT_FAMILY,
          },
        },
        beginAtZero: true,
        border: {
          display: false,
        },
        grid: {
          color: DEFAULT_LIGHT_THEME_BORDER_COLOR,
        },
        ticks: {
          stepSize: 0.1,
          color: DEFAULT_LIGHT_THEME_BORDER_COLOR,
          font: {
            family: DEFAULT_FONT_FAMILY,
          },
          callback: (label) => {
            return label;
          },
        },
      },
    },
    plugins: {
      datalabels: {
        display: false,
      },
      spanGaps: true,
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          pointStyle: 'circle',
          usePointStyle: true,
          color: DEFAULT_LIGHT_THEME_TEXT_COLOR,
          font: {
            family: DEFAULT_FONT_FAMILY,
          },
        },
      },
      tooltip: {
        boxWidth: 10,
        boxHeight: 10,
        boxPadding: 4,
        cornerRadius: 4,
        usePointStyle: true,
        bodyFont: {
          family: DEFAULT_FONT_FAMILY,
        },
        callbacks: {
          label: (tooltipItem) => {
            return `${tooltipItem.dataset.label}: ${tooltipItem.formattedValue}`;
          },
        },
      },
    },
  },
};

export const DEVICE_GPS_UPDATE_TIMER_MIN = 3;

export const PORTAL_FAQ_MAPPING = {
  needsAttention: {
    faqId: 'NEEDS_ATTENTION',
    faqVersion: 'v1',
  },
  panicButtonEvents: {
    faqId: 'PANIC_BUTTON_EVENTS',
    faqVersion: 'v1',
  },
  deviceDiagnostisSummary: {
    faqId: 'DEVICE_DIAGNOSTICS_SUMMARY',
    faqVersion: 'v1',
  },
  safetyTrends: {
    faqId: 'SAFETY_TRENDS',
    faqVersion: 'v1',
  },
  safetyTrendsAssets: {
    faqId: 'SAFETY_TRENDS_ASSET',
    faqVersion: 'v1',
  },
  fleetHighlights: {
    faqId: 'FLEET_HIGHLIGHTS',
    faqVersion: 'v1',
  },
  topDrivers: {
    faqId: 'TOP_DRIVERS',
    faqVersion: 'v1',
  },
  requireCoaching: {
    faqId: 'COACHABLE_DRIVERS',
    faqVersion: 'v1',
  },
  topAssets: {
    faqId: 'TOP_ASSETS',
    faqVersion: 'v1',
  },
  coachableAssets: {
    faqId: 'COACHABLE_ASSETS',
    faqVersion: 'v1',
  },
  incidentSummary: {
    faqId: 'INCIDENT_SUMMARY',
    faqVersion: 'v1',
  },
  incidentTrend: {
    faqId: 'INCIDENT_TREND',
    faqVersion: 'v1',
  },
  tripList: {
    faqId: 'TRIP_LIST',
    faqVersion: 'v1',
  },
  activeDrivers: {
    faqId: 'ACTIVE_DRIVERS',
    faqVersion: 'v1',
  },
  videoRequests: {
    faqId: 'VIDEO_REQUESTS',
    faqVersion: 'v1',
  },
  coachingPanel: {
    faqId: 'COACHING_PANEL',
    faqVersion: 'v1',
  },
  challengedIncidents: {
    faqId: 'CHALLENGED_INCIDENTS',
    faqVersion: 'v1',
  },
  driverHighlights: {
    faqId: 'DRIVER_HIGHLIGHTS',
    faqVersion: 'v1',
  },
  driverIncidentSummary: {
    faqId: 'DRIVER_INCIDENT_SUMMARY',
    faqVersion: 'v1',
  },
  driverIncidentTrend: {
    faqId: 'DRIVER_INCIDENT_TREND',
    faqVersion: 'v1',
  },
  driverList: {
    faqId: 'UNIFIED_DRIVER',
    faqVersion: 'v1',
  },
  mostImproved: {
    faqId: 'MOST_IMPROVED',
    faqVersion: 'v1',
  },
  assetList: {
    faqId: 'ASSET_LIST',
    faqVersion: 'v1',
  },
  basicConfigurations: {
    faqId: 'BASIC_CONFIGURATIONS',
    faqVersion: 'v1',
  },
  advancedConfigurations: {
    faqId: 'ADVANCED_CONFIGURATIONS',
    faqVersion: 'v1',
  },
  incidentsView: {
    faqId: 'INCIDENT_VIEW',
    faqVersion: 'v2',
  },
  recommendedIncidents: {
    faqId: 'RECOMMENDED_INCIDENTS',
    faqVersion: 'v1',
  },
  eventsHighlights: {
    faqId: 'EVENTS_HIGHLIGHTS',
    faqVersion: 'v1',
  },
  driverConfigurations: {
    faqId: 'DRIVER_CONFIGURATIONS',
    faqVersion: 'v1',
  },
  CoachableDrivers: {
    faqId: 'COACHABLE_DRIVERS_DETAILS',
    faqVersion: 'v1',
  },
  CompletedCoachingSessions: {
    faqId: 'COMPLETED_COACHING_SESSION',
    faqVersion: 'v1',
  },
  ClosedCoachingSessions: {
    faqId: 'CLOSED_COACHING_SESSION',
    faqVersion: 'v1',
  },
  CoachingThresholds: {
    faqId: 'COACHING_THERSHOLDS',
    faqVersion: 'v1',
  },
  SelfCoachingThresholds: {
    faqId: 'SELF_COACHING_THERSHOLDS',
    faqVersion: 'v1',
  },
  TaggingOverview: {
    faqId: 'TAGGING_OVERVIEW',
    faqVersion: 'v1',
  },
  AttributeList: {
    faqId: 'ATTRIBUTES_LIST',
    faqVersion: 'v1',
  },
  TagsList: {
    faqId: 'TAGS_LIST',
    faqVersion: 'v1',
  },
  EntityList: {
    faqId: 'ENTITY_LIST',
    faqVersion: 'v1',
  },
  ManageRoles: {
    faqId: 'MANAGE_ROLES',
    faqVersion: 'v1',
  },
  ManageUsers: {
    faqId: 'MANAGE_USERS',
    faqVersion: 'v1',
  },
  ActivityLog: {
    faqId: 'USER_ACTIVITY_LOG',
    faqVersion: 'v1',
  },
  SemiProvisionedDevices: {
    faqId: 'SEMI_PROVISIONED_DEVICES',
    faqVersion: 'v1',
  },
  AutomatedCoaching: {
    faqId: 'AUTOMATED_COACHING',
    faqVersion: 'v1',
  },
  RecognizedEvents: {
    faqId: 'RECOGNIZED_EVENTS',
    faqVersion: 'v1',
  },
  EventStreak: {
    faqId: 'EVENT_STREAK',
    faqVersion: 'v1',
  },
  AIRecognizedEvents: {
    faqId: 'AI_RECOGNIZED_EVENTS',
    faqVersion: 'v1',
  },
  ExportAsyncDownloadHistory: {
    faqId: 'EXPORT_ASYNC_DOWNLOAD_HISTORY',
    faqVersion: 'v1',
  },
  scheduledReports: {
    faqId: 'SCHEDULED_REPORTS',
    faqVersion: 'v1',
  },
  Installers: {
    faqId: 'UNIFIED_INSTALLER',
    faqVersion: 'v1',
  },
  AssetCentric: {
    faqId: 'ASSET_CENTRIC',
    faqVersion: 'v1',
  },
  DriverCentric: {
    faqId: 'DRIVER_CENTRIC',
    faqVersion: 'v1',
  },
};

export const PORTAL_CONFIG_FAQ_MAPPING = {
  'Harsh Acceleration': {
    faqId: 'HARSH_ACCELERATION',
    faqVersion: 'v1',
  },
  'Harsh Braking': {
    faqId: 'HARSH_BREAKING',
    faqVersion: 'v1',
  },
  'Harsh Cornering': {
    faqId: 'HARSH_CORNERING',
    faqVersion: 'v1',
  },
  'Speed Limit Violation': {
    faqId: 'SPEEDLIMIT_VIOLATION',
    faqVersion: 'v1',
  },
  'Posted Speed': {
    faqId: 'POSTED_SPEED',
    faqVersion: 'v1',
  },
  'Maximum Speed': {
    faqId: 'MAXIMUM_SPEED',
    faqVersion: 'v1',
  },
  'Lane Drift': {
    faqId: 'LANE_DRIFT',
    faqVersion: 'v1',
  },
  Tailgating: {
    faqId: 'TAILGATING',
    faqVersion: 'v1',
  },
  'Stop Sign Violation': {
    faqId: 'STOP_SIGN_VIOLATION',
    faqVersion: 'v1',
  },
  'Distracted Driving': {
    faqId: 'DISTRACTED_DRIVING',
    faqVersion: 'v1',
  },
  'Head Pose Deviation': {
    faqId: 'HEAD_POSE_DEVIATION',
    faqVersion: 'v1',
  },
  'Gaze Down Distraction': {
    faqId: 'GAZE_DOWN_DISTRACTION',
    faqVersion: 'v1',
  },
  'Smoking Distraction': {
    faqId: 'SMOKING_DISTRACTION',
    faqVersion: 'v1',
  },
  'Drinking Distraction': {
    faqId: 'DRINKING_DISTRACTION',
    faqVersion: 'v1',
  },
  'Texting Distraction': {
    faqId: 'TEXTING_DISTRACTION',
    faqVersion: 'v1',
  },
  'Cellphone Distraction': {
    faqId: 'CELLPHONE_DISTRACTION',
    faqVersion: 'v1',
  },
  'Forward Collision Warning': {
    faqId: 'FORWARD_COLLISION',
    faqVersion: 'v1',
  },
  'Drowsy Driving': {
    faqId: 'DROWSY_DRIVING',
    faqVersion: 'v1',
  },
  'Seatbelt Violation': {
    faqId: 'SEATBELT_VIOLATION',
    faqVersion: 'v1',
  },
  'Traffic Light Violation': {
    faqId: 'TRAFFIC_LIGHT_VIOLATION',
    faqVersion: 'v1',
  },
  'Rollover Detection': {
    faqId: 'ROLLOVER_DETECTION',
    faqVersion: 'v1',
  },
  'Surveillance Mode': {
    faqId: 'SURVEILLANCE_MODE',
    faqVersion: 'v1',
  },
  Device: {
    faqId: 'DEVICE',
    faqVersion: 'v1',
  },
  Location: {
    faqId: 'LOCATION',
    faqVersion: 'v1',
  },
  Other: {
    faqId: 'OTHER',
    faqVersion: 'v1',
  },
  'High-G Event': {
    faqId: 'POSSIBLE_COLLISION',
    faqVersion: 'v1',
  },
  'Privacy Mode': {
    faqId: 'DRIVER_PRIVACY_MODE',
    faqVersion: 'v1',
  },
  'Panic Button': {
    faqId: 'PANIC_BUTTON',
    faqVersion: 'v1',
  },
  Fatigue: {
    faqId: 'DRIVER_FATIGUE',
    faqVersion: 'v1',
  },
  'Event Based Actions': {
    faqId: 'GPIO_TRIGGER',
    faqVersion: 'v1',
  },
};

export interface AsyncReportConfig {
  key: string;
  label: string;
  featureFlag?: string;
  additionalParams?: string[];
  reportConfig?: {
    showDuration?: boolean;
    showTags?: boolean;
    showFilterType?: boolean;
    showEventList?: boolean;
  };
}

export const TAG_REQUIRED_REPORT_TYPES = new Set<string>([
  'EVENT_LIST',
  'EVENT_COUNT',
  'POTENTIAL_CRASH_EVENTS',
  'FLEET_DVR_REQUESTS',
  'PRIVACY_MODE',
  'PANIC_EVENTS',
]);

export const AsyncReportType = {
  trips: {
    key: 'TRIPS',
    label: 'Trip List',
    featureFlag: 'trip_list_page',
  },
  drivers: {
    key: 'FLEET_DRIVERS',
    label: 'Driver List',
  },
  fleetSafety: {
    key: 'FLEET_SAFETY',
    label: 'Fleet Safety Report',
  },
  devicePing: {
    key: 'DEVICE_PING',
    label: 'Device Online Status',
    featureFlag: 'diagnostics_tab',
  },
  coachingSession: {
    key: 'COACHING_SESSION',
    label: 'Coaching Session Report',
    featureFlag: 'reports:coaching_session_report',
  },
  selfCoachingEffectiveness: {
    key: 'SELF_COACHING_EFFECTIVENESS',
    label: 'Self-Coaching Effectiveness (Driver)',
    featureFlag: 'reports:self_coaching_effectiveness_report',
  },
  fleetCameraEvents: {
    key: 'FLEET_CAMERA_EVENTS',
    label: 'Camera Visibility Events',
    featureFlag: 'diagnostics_tab',
  },
  fleetHealthEvents: {
    key: 'FLEET_HEALTH_EVENTS',
    label: 'Device Diagnostic Events',
    featureFlag: 'diagnostics_tab',
  },
  driverCoachingEffectiveness: {
    key: 'DRIVER_COACHING_EFFECTIVENESS',
    label: 'Coaching Effectiveness Report (Driver)',
    featureFlag: 'reports:coaching_effectiveness_report',
  },
  supressedDiagnosticsEventsReport: {
    key: 'FLEET_SUPRESSED_DIAGNOSTICS_EVENTS',
    label: 'Muted Camera Visibility Events',
    featureFlag: 'diagnostics_tab',
  },
  privacyModeEventsReport: {
    key: 'PRIVACY_MODE',
    label: 'Privacy Mode Report',
    reportConfig: {
      showDuration: true,
      showTags: true,
      showFilterType: false,
    },
  },
  eventListReport: {
    key: 'EVENT_LIST',
    label: 'Event List Report',
    additionalParams: ['unitSystem', 'language'],
    reportConfig: {
      showDuration: true,
      showTags: true,
      showFilterType: true,
      showEventList: true,
    },
  },
  eventCountReport: {
    key: 'EVENT_COUNT',
    label: 'Event Count Report',
    reportConfig: {
      showDuration: true,
      showTags: true,
      showFilterType: true,
    },
  },
  customReport: {
    key: 'CUSTOM_REPORT',
    label: 'Custom Report',
    reportConfig: {
      showDuration: true,
      showTags: true,
      showFilterType: true,
    },
  },
  fleetAssetReport: {
    key: 'FLEET_ASSETS_REPORT',
    label: 'Asset List Report',
  },
  fleetDriverQRCode: {
    key: 'FLEET_DRIVERS_QR_CODES',
    label: 'Fleet Driver QR Code',
  },
  potentialCrashEvent: {
    key: 'POTENTIAL_CRASH_EVENTS',
    label: 'High-G Event List Report',
    reportConfig: {
      showDuration: true,
      showTags: true,
      showFilterType: false,
    },
  },
  videoRequestListReport: {
    key: 'FLEET_DVR_REQUESTS',
    label: 'Video Request List Report',
    reportConfig: {
      showDuration: true,
      showTags: true,
      showFilterType: true,
    },
  },
  panicButtonListReport: {
    key: 'PANIC_EVENTS',
    label: 'Panic Button List Report',
    reportConfig: {
      showDuration: true,
      showTags: true,
      showFilterType: false,
    },
  },
  geofenceActivityReport: {
    key: 'FLEET_GEOFENCES',
    label: 'Geofence Activity Report',
    reportConfig: {
      showDuration: true,
      showGeofence: true,
    },
  },
  driverBehaviourSummary: {
    key: 'DRIVER_BEHAVIOUR_SUMMARY',
    label: 'Driver Behaviour Summary',
  },
};

export const ScheduledReportType = {
  ...AsyncReportType,
  fleetdevicePing: {
    key: 'FLEET_DEVICE_PING',
    label: 'Device Online Status',
  },
};

export const COGNITO_REGIONS_MAPPING: { [key: string]: CognitoRegionType[] } = {
  'us-east-1': [
    { label: 'regionLabel.USA', value: 'USA' },
    { label: 'regionLabel.Canada', value: 'CAN' },
    { label: 'regionLabel.LATAM', value: 'LATAM' },
    { label: 'regionLabel.Africa', value: 'AFRICA' },
    { label: 'regionLabel.Other', value: 'OTHER' },
  ],
  'ap-southeast-2': [{ label: 'regionLabel.Australia', value: 'AUS' }],
  'eu-central-1': [{ label: 'regionLabel.EU', value: 'EU' }],
  'eu-west-2': [{ label: 'regionLabel.UK', value: 'UK' }],
  'me-south-1': [{ label: 'regionLabel.Middle East', value: 'ME' }],
  'ap-south-1': [{ label: 'regionLabel.India', value: 'IND' }],
  'sa-east-1': [{ label: 'regionLabel.Brazil', value: 'BRA' }],
};

export const FEATURE_ANNOUNCEMENT_LIST = [
  {
    featureImage: 'assets/common/feature1-fp.svg',
    featureName: 'Portal Notifications',
    featureDescriptionList: [
      {
        description: 'Stay informed with real-time notifications tailored to update you on critical device health events.',
      },
    ],
  },
];

// increment the version number when showing new feature announcement
export const FEATURE_ANNOUNCEMENT_VERSION = 'feature-announcement-v18';

export const PORTAL_RELEASE_VERSION = 'v11.7.0';

export const ENTITIES = {
  trip: {
    key: 'trip',
    label: 'Trip',
  },
  event: {
    key: 'event',
    label: 'Event',
  },
  driver: {
    key: 'driver',
    label: 'Driver',
  },
  asset: {
    key: 'asset',
    label: 'Asset',
  },
  'dvr-video': {
    key: 'dvr-video',
    label: 'Video Request',
  },
};

export const COOKIE_CONSENT_EXPIRES = 180 * 24 * 60 * 60 * 1000;

export const DELETE_COOKIES_START_TIME = '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';

export const DO_NOT_DELETE_LIST = [
  'LOGIN_INFO',
  'current-cookies-consent',
  'feature-announcement-v',
  'current-fleet',
  'aws-cognito-region',
  'current-metric-unit',
];
export const MANAGE_USERS_TABLE_COLUMNS = ['email', 'name', 'role', 'status', 'fleets', 'creationDate', 'actions'];
export const MANAGE_ROLES_TABLE_COLUMNS = [
  'name',
  'level',
  'attributes',
  'createdOn',
  'createdBy',
  'lastUpdatedOn',
  'lastUpdatedBy',
  'status',
  'actions',
];

export const speedSignTagFilter = [
  {
    key: 'Work',
    label: 'Workzone (BETA)',
  },
];

export enum speedSignTag {
  Work = 'Work',
  School = 'School',
}

export const FLEETMANAGER_MAX_COMMENT = 10;

export const MIXPANEL_INIT_KEY_PROD = '989b75a55e52a03980a9e99b84e13f08';

export const MIXPANEL_INIT_KEY_QA = '584e20e84e557096fc50f0e977f46c98';

export const MIXPANEL_INIT_KEY = '989b75a55e52a03980a9e99b84e13f08';

export const SEGMENT_WRITE_KEY = '29yc34ppyD6AhsR1VcyiWA7nTbepXElp';

export const HIGH_COLOR = {
  text: '#D70000',
  background: '#FFD7D7',
};

export const MEDIUM_COLOR = {
  text: '#BB9407',
  background: '#FFF3C7',
};

export const LOW_COLOR = {
  text: '#0F9514',
  background: '#DAFFDB',
};

export const PINNED_REPORTS_KEY = 'pinnedReports';

export const TABLE_PAGESIZE_KEY = 'current-table-pagesize';
export const DEFAULT_TABLE_PAGESIZE = 30;
export const INCIDENT_QUICK_ACTION_MENU_ITEM = [
  { action: 'MARK_AS_VIEWED', label: 'Mark_as_Viewed' },
  { action: 'SELECT_FOR_COACHING', label: 'commonIncidentViewSelectForCoaching' },
  { action: 'DISCARD_EVENTS', label: 'Discard_Event(s)' },
];
export const SEVERITY_EVENT_FILTER_LIST = [
  {
    label: 'eventsTableHigh',
    value: 'high',
  },
  {
    label: 'eventsTableMedium',
    value: 'medium',
  },
  {
    label: 'eventsTableLow',
    value: 'low',
  },
];

export const VIEWED_STATUS_FILTER_LIST = [
  { label: 'eventsTableAll', value: 'All' },
  { label: 'Viewed', value: 'true' },
  { label: 'New', value: 'false' },
];

export const SUPRSEND_WORKSPACE_KEY = environment.env === 'prod' ? 'YAsPc5JhjjUphtNlhAM8' : 'DIRFCyKpDtRMrELNMVxd';

export const SUPRSEND_PUBLIC_API_KEY =
  environment.env === 'prod'
    ? 'SS.PUBK.W_FzsL0P9V3ri4WJXDO7RfHWrXYx1Q0tgbcw9sZWyO0'
    : 'SS.PUBK.01pT86hTauJ_Le5lkbb1MgTBjSJymbHP_b3q9hBi9o4';

export const SUPRSEND_CSS_CLASS_PREFIX = 'preact-shadow';

// Translation URL pattern
// {portal} is always fleet
// {env} is env.translation_env or env.env
// {version} is env.translation_version or PORTAL_RELEASE_VERSION
// {lang} is dynamically set based on the user language
export const TRANSLATION_URL_PATTERN = `https://assets.lightmetrics.co/translations/{portal}/{env}/{version}/{lang}.json?clientId={clientId}`;

export const PORTAL_NOTIFICATION_EVENT_FLAGS = [
  'Cornering',
  'Harsh-Acceleration',
  'Harsh-Braking',
  'Cellphone-Distracted-Driving',
  'Smoking-Distracted-Driving',
  'Drinking-Distracted-Driving',
  'Distracted-Driving',
  'Lizard-Eye-Distracted-Driving',
  'Texting-Distracted-Driving',
  'Drowsy-Driving-Detected',
  'Driver-Fatigue-Detected',
  //'MaxSpeedExceeded',
  'Traffic-Speed-Violated',
  'Unbuckled-Seat-Belt',
  'Tail-Gating-Detected',
  'Forward-Collision-Warning',
  'Traffic-STOP-Sign-Violated',
  'Traffic-Light-Violated',
  'Lane-Drift-Found',
  'Custom-Triggered',
  'PotentialCrash',
  'DVRUpload',
  'IN_PERSON_COACHING_SCHEDULED',
  'Roll-Over-Detected',
  'geofence',
];

export const DEFAULT_MAX_ALLOWED_TAGS = 20;

export const GEOFENCE_MIN_POLYGON_POINT = 3;

export const SELECTED_ACTIVE_CUSTOMERS_EULA_CONFIGS: { [customerName in string]: EULA_CONFIG } = {
  platformscience: {
    skipStep2: false,
    template: 'general',
  },
  satrack: {
    skipStep2: true,
    template: 'satrack',
  },
  satrackstaging: {
    skipStep2: true,
    template: 'satrack',
  },
  mx4track: {
    skipStep2: true,
    template: 'mx4track',
  },
  // lmqatesting1: {
  //   skipStep2: true,
  //   template: 'mx4track',
  // },
};
